// Wizard step 2 — program structure (slice 4a-3).
//
// Stamps required (5–15, the existing loyalty range), the milestone list, and
// completion behavior. The grand prize is the single `type:'final'` milestone,
// auto-pinned to `at_stamp === total_stamps` and labelled "Grand prize" — the
// owner edits its reward text but not its position. Intermediate milestones
// can be added (up to total_stamps − 1) and removed.
//
// These are STRUCTURAL fields — locked once the program is active. The wizard
// passes `readOnly` in that case.

import {
  COMPLETION_BEHAVIORS,
  LOYALTY_MAX_STAMPS,
  LOYALTY_MIN_STAMPS,
} from '@naya/shared'
import type { CompletionBehaviorType, Milestone } from '@naya/shared'

import { grandPrize } from '../builderState.js'
import type { BuilderController } from '../hooks/useBuilder.js'
import { ClampedNumberInput } from './ClampedNumberInput.js'
import { ExpirationFields } from './ExpirationFields.js'

const COMPLETION_COPY: Record<
  CompletionBehaviorType,
  { label: string; hint: string }
> = {
  reset: {
    label: 'Reset for another round',
    hint: "Card stays in the customer's wallet, stamps reset to 0, and a completions counter goes up.",
  },
  stay_completed: {
    label: 'Mark complete forever',
    hint: "Card shows 'Completed' permanently once the grand prize is claimed.",
  },
}

// Small contextual warning shown next to a structural field's label instead
// of one generic banner at the top of the step — a warning noticed right
// where the irreversible choice is made beats a disclaimer read once and
// forgotten by the time the owner reaches the third decision on the step.
function LockBadge() {
  return (
    <span className="program-lock-badge">
      <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <rect x="5" y="11" width="14" height="9" rx="2" stroke="currentColor" strokeWidth="1.8" />
        <path d="M8 11V8a4 4 0 0 1 8 0v3" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      </svg>
      Locks after activation
    </span>
  )
}

function Stepper({
  value,
  min,
  max,
  disabled,
  onChange,
}: {
  value: number
  min: number
  max: number
  disabled?: boolean
  onChange: (v: number) => void
}) {
  return (
    <div className="program-stepper">
      <button
        type="button"
        className="owner-btn program-stepper-btn"
        disabled={disabled || value <= min}
        onClick={() => onChange(value - 1)}
        aria-label="Decrease"
      >
        −
      </button>
      <ClampedNumberInput
        className="owner-input program-stepper-input"
        min={min}
        max={max}
        value={value}
        disabled={disabled}
        onCommit={onChange}
        ariaLabel="Stamps required"
      />
      <button
        type="button"
        className="owner-btn program-stepper-btn"
        disabled={disabled || value >= max}
        onClick={() => onChange(value + 1)}
        aria-label="Increase"
      >
        +
      </button>
    </div>
  )
}

function MilestoneRow({
  milestone,
  totalStamps,
  readOnly,
  onChange,
  onRemove,
}: {
  milestone: Milestone
  totalStamps: number
  readOnly: boolean
  onChange: (next: Milestone) => void
  onRemove?: () => void
}) {
  const isGrand = milestone.type === 'final'
  return (
    <div className={`program-milestone-row${isGrand ? ' is-grand' : ''}`}>
      <div className="program-milestone-head">
        {isGrand ? (
          <span className="program-milestone-badge">
            Grand prize · stamp {totalStamps}
          </span>
        ) : (
          <label className="program-milestone-at">
            <span>At stamp</span>
            <ClampedNumberInput
              className="owner-input"
              min={1}
              max={totalStamps - 1}
              value={milestone.at_stamp}
              disabled={readOnly}
              onCommit={(at_stamp) => onChange({ ...milestone, at_stamp })}
              ariaLabel="Milestone stamp position"
            />
          </label>
        )}
        {!isGrand && onRemove && !readOnly && (
          <button
            type="button"
            className="program-link-btn program-danger"
            onClick={onRemove}
          >
            Remove
          </button>
        )}
      </div>
      <input
        type="text"
        className="owner-input"
        placeholder={
          isGrand ? 'e.g. Free large milk tea' : 'e.g. 50% off any drink'
        }
        value={milestone.reward_text}
        maxLength={120}
        disabled={readOnly}
        onChange={(e) => onChange({ ...milestone, reward_text: e.target.value })}
        aria-label={isGrand ? 'Grand prize reward' : 'Milestone reward'}
      />
    </div>
  )
}

export function StepProgram({
  builder,
  readOnly = false,
}: {
  builder: BuilderController
  readOnly?: boolean
}) {
  const { draft } = builder
  const total = draft.total_stamps
  const milestones = draft.milestones
  const intermediates = milestones.filter((m) => m.type !== 'final')
  const grand = grandPrize(milestones)
  // Max intermediate milestones = total - 1 (every stamp slot except where the
  // grand prize already sits).
  const canAddMilestone = !readOnly && intermediates.length < total - 1

  function setMilestones(next: Milestone[]) {
    builder.update({ milestones: next })
  }

  function updateMilestone(index: number, next: Milestone) {
    setMilestones(milestones.map((m, i) => (i === index ? next : m)))
  }

  function removeMilestone(index: number) {
    setMilestones(milestones.filter((_, i) => i !== index))
  }

  function addMilestone() {
    // Drop the new milestone at the first free stamp position below total.
    const used = new Set(milestones.map((m) => m.at_stamp))
    let at = 1
    while (at < total && used.has(at)) at += 1
    const next: Milestone = { at_stamp: at, reward_text: '', type: 'intermediate' }
    // Keep the grand prize last in the array.
    setMilestones([...intermediates, next, ...(grand ? [grand] : [])])
  }

  return (
    <div className="program-step">
      <h2>Set up the stamp card</h2>
      <p className="program-step-sub">
        A few quick choices for how the card works.
      </p>

      <div className="program-field-block">
        <span className="program-field-label-row">
          <span className="program-field-label">Stamps required</span>
          {!readOnly && <LockBadge />}
        </span>
        <span className="program-field-hint">
          How many stamps to reach the grand prize. {LOYALTY_MIN_STAMPS}–
          {LOYALTY_MAX_STAMPS}.
        </span>
        <Stepper
          value={total}
          min={LOYALTY_MIN_STAMPS}
          max={LOYALTY_MAX_STAMPS}
          disabled={readOnly}
          onChange={(v) => builder.update({ total_stamps: v })}
        />
      </div>

      <div className="program-field-block">
        <span className="program-field-label-row">
          <span className="program-field-label">Rewards</span>
          {!readOnly && <LockBadge />}
        </span>
        <span className="program-field-hint">
          The grand prize is required. Add intermediate rewards if you want to
          keep customers motivated along the way.
        </span>
        <div className="program-milestone-list">
          {intermediates.map((m) => {
            const index = milestones.indexOf(m)
            return (
              <MilestoneRow
                key={index}
                milestone={m}
                totalStamps={total}
                readOnly={readOnly}
                onChange={(next) => updateMilestone(index, next)}
                onRemove={() => removeMilestone(index)}
              />
            )
          })}
          {grand && (
            <MilestoneRow
              milestone={grand}
              totalStamps={total}
              readOnly={readOnly}
              onChange={(next) => updateMilestone(milestones.indexOf(grand), next)}
            />
          )}
        </div>
        {canAddMilestone && (
          <button
            type="button"
            className="owner-btn program-add-btn"
            onClick={addMilestone}
          >
            + Add milestone
          </button>
        )}
      </div>

      <div className="program-field-block">
        <span className="program-field-label-row">
          <span className="program-field-label">When the card is completed</span>
          {!readOnly && <LockBadge />}
        </span>
        <div className="program-radio-list">
          {COMPLETION_BEHAVIORS.map((behavior) => (
            <label
              key={behavior}
              className={`program-radio-row${
                draft.completion_behavior === behavior ? ' is-selected' : ''
              }`}
            >
              <input
                type="radio"
                name="completion_behavior"
                checked={draft.completion_behavior === behavior}
                disabled={readOnly}
                onChange={() =>
                  builder.update({ completion_behavior: behavior })
                }
              />
              <span className="program-radio-main">
                <span className="program-field-label">
                  {COMPLETION_COPY[behavior].label}
                </span>
                <span className="program-field-hint">
                  {COMPLETION_COPY[behavior].hint}
                </span>
              </span>
            </label>
          ))}
        </div>
      </div>

      {/* Pass expiration — only offered for 'stay_completed' programs. A
          'reset' card recycles forever, so expiring it would kill an engaged
          customer's in-progress card. */}
      {draft.completion_behavior === 'stay_completed' && (
        <ExpirationFields builder={builder} readOnly={readOnly} />
      )}
    </div>
  )
}
